document.addEventListener('DOMContentLoaded', () => {
  const accountComponent = document.querySelector('[data-account-component]');
  if (accountComponent && document.body.dataset.customerAuthenticated !== 'true') {
    accountComponent.addEventListener('close', () => {
      // The account sheet signs customers in without leaving the storefront.
      // Reload so Shopify can render the Liquid customer and their order-backed files.
      window.location.reload();
    });
  }

  const quantityPricingConfig = (scope) => {
    if (!scope || scope.dataset.quantityPricingEnabled !== 'true') return { enabled: false, tiers: [] };
    const tiers = [1, 2, 3].map((number) => ({
      quantity: Number(scope.dataset[`tier${number}Quantity`]),
      percent: Number(scope.dataset[`tier${number}Percent`])
    })).filter((tier) => tier.quantity > 0 && tier.percent > 0)
      .sort((left, right) => left.quantity - right.quantity);
    return { enabled: tiers.length > 0, tiers };
  };

  const renderQuantityTier = ({ count, config, status, detail, bar }) => {
    if (!config.enabled || !status) return;
    const current = [...config.tiers].reverse().find((tier) => count >= tier.quantity);
    const next = config.tiers.find((tier) => count < tier.quantity);
    if (current) {
      status.textContent = `${current.percent}% quantity pricing unlocked!`;
    } else if (next) {
      status.textContent = `Add ${next.quantity - count} more eligible ${next.quantity - count === 1 ? 'piece' : 'pieces'} to unlock ${next.percent}% quantity pricing.`;
    }
    if (detail) {
      if (next) {
        detail.textContent = `${count} eligible ${count === 1 ? 'piece' : 'pieces'} total · ${next.quantity - count} more to unlock ${next.percent}% off.`;
      } else {
        detail.textContent = `${count} eligible pieces total · Highest quantity tier unlocked.`;
      }
    }
    if (bar) {
      const target = next?.quantity || config.tiers[config.tiers.length - 1].quantity;
      bar.style.width = `${Math.min(100, Math.max(0, (count / target) * 100))}%`;
    }
  };

  const previewColors = {
    black: '#17191b',
    charcoal: '#3d4144',
    navy: '#172b49',
    'royal blue': '#2456a6',
    'forest green': '#244b36',
    red: '#a52a32',
    white: '#eeeae2',
    'safety orange': '#f26b21'
  };

  const showColorPreview = (select) => {
    const scope = select.closest('[data-product-card], .product-page');
    const image = scope?.querySelector('[data-product-preview]');
    const note = scope?.querySelector('[data-color-preview-note]');
    const option = select.selectedOptions[0];
    if (!scope || !image || !option) return;

    const colorName = option.dataset.color || '';
    const exactImageUrl = option.dataset.imageUrl || '';
    const currentRequest = String(Number(scope.dataset.previewRequest || 0) + 1);
    scope.dataset.previewRequest = currentRequest;

    let canvas = scope.querySelector('[data-color-preview-canvas]');
    if (exactImageUrl) {
      image.src = exactImageUrl;
      image.removeAttribute('srcset');
      image.alt = `${image.alt.split(' – ')[0]} – ${colorName}`;
      image.hidden = false;
      if (canvas) canvas.hidden = true;
      if (note) note.textContent = `${colorName} preview`;
      return;
    }

    const targetHex = previewColors[colorName.toLowerCase()];
    const baseSource = image.dataset.basePreview;
    if (!targetHex || !baseSource) {
      image.hidden = false;
      if (canvas) canvas.hidden = true;
      if (note) note.textContent = colorName ? `${colorName} selected` : '';
      return;
    }

    const source = new Image();
    source.crossOrigin = 'anonymous';
    source.onload = () => {
      if (scope.dataset.previewRequest !== currentRequest) return;
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.dataset.colorPreviewCanvas = '';
        canvas.setAttribute('role', 'img');
        image.insertAdjacentElement('afterend', canvas);
      }

      const width = source.naturalWidth;
      const height = source.naturalHeight;
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.clearRect(0, 0, width, height);
      context.drawImage(source, 0, 0, width, height);

      const pixels = context.getImageData(0, 0, width, height);
      const target = targetHex.match(/[a-f\d]{2}/gi).map((part) => parseInt(part, 16));
      for (let index = 0; index < pixels.data.length; index += 4) {
        const red = pixels.data[index];
        const green = pixels.data[index + 1];
        const blue = pixels.data[index + 2];
        const alpha = pixels.data[index + 3];
        if (alpha < 8) continue;

        const maximum = Math.max(red, green, blue);
        const minimum = Math.min(red, green, blue);
        const saturation = maximum === 0 ? 0 : (maximum - minimum) / maximum;
        const luminance = (red * 0.2126 + green * 0.7152 + blue * 0.0722) / 255;

        // Preserve the teal and white ModiThread logo while recoloring neutral fabric.
        if (saturation > 0.16 || luminance > 0.82) continue;
        const shade = Math.min(1.7, Math.max(0.18, luminance / 0.24));
        for (let channel = 0; channel < 3; channel += 1) {
          pixels.data[index + channel] = shade <= 1
            ? Math.round(target[channel] * shade)
            : Math.round(target[channel] + (255 - target[channel]) * ((shade - 1) / 0.7));
        }
      }
      context.putImageData(pixels, 0, 0);
      canvas.setAttribute('aria-label', `${colorName} color preview`);
      canvas.hidden = false;
      image.hidden = true;
      if (note) note.textContent = `${colorName} preview`;
    };
    source.onerror = () => {
      image.hidden = false;
      if (canvas) canvas.hidden = true;
      if (note) note.textContent = `${colorName} selected`;
    };
    source.src = baseSource;
  };

  document.querySelectorAll('[data-preview-select]').forEach((select) => {
    select.addEventListener('change', () => showColorPreview(select));
    showColorPreview(select);
  });

  const builder = document.querySelector('#ModiThreadBuilder');
  if (builder) {
    const mobileBuildCta = document.querySelector('.mobile-build-cta');
    if (mobileBuildCta && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        mobileBuildCta.classList.toggle('is-hidden', entries.some((entry) => entry.isIntersecting));
      }, { threshold: 0.05 }).observe(builder);
    }
    const cards = [...builder.querySelectorAll('[data-product-card]')];
    const message = builder.querySelector('[data-builder-message]');
    const submit = builder.querySelector('button[type="submit"]');
    const fileInput = document.querySelector('#CustomerLogo');
    const logoForm = document.querySelector('#LogoSetupForm');
    const proofConfirmation = document.querySelector('#ProofConfirmation');
    const logoSourceInputs = [...builder.querySelectorAll('input[name="logo_source"]')];
    const savedLogoSelect = document.querySelector('#SavedLogoSelect');
    const logoSetupLine = builder.querySelector('[data-logo-setup-line]');
    const draftLines = builder.querySelector('[data-draft-lines]');
    const builderTierConfig = quantityPricingConfig(builder);
    const cartEligibleQuantity = Number(builder.dataset.cartEligibleQuantity || 0);
    const placementNames = {
      'left-chest': 'Left chest',
      'right-chest': 'Right chest',
      'center-front': 'Middle front',
      'back-shoulders': 'Back between shoulders'
    };
    const placementSettings = {
      'left-chest': { x: 39, y: 33, maximum: 4.5, view: 'front' },
      'right-chest': { x: 61, y: 33, maximum: 4.5, view: 'front' },
      'center-front': { x: 50, y: 47, maximum: 10, view: 'front' },
      'back-shoulders': { x: 50, y: 30, maximum: 8, view: 'back' }
    };
    let customerLogoUrl = '';
    let customerLogoIsObjectUrl = false;

    const selectedLogoSource = () => {
      const checked = builder.querySelector('input[name="logo_source"]:checked');
      return checked?.value || builder.querySelector('input[name="logo_source"]')?.value || 'new';
    };

    const displayCustomerLogo = (url, statusMessage) => {
      customerLogoUrl = url;
      builder.querySelectorAll('[data-logo-editor]').forEach((editor) => {
        const logo = editor.querySelector('[data-proof-logo]');
        const empty = editor.querySelector('[data-proof-empty]');
        logo.src = url;
        logo.hidden = !url;
        empty.hidden = Boolean(url);
      });
      if (statusMessage) message.textContent = statusMessage;
    };

    const selectedRows = (card) => [...card.querySelectorAll('[data-variant-row]')];

    const syncVariantRow = (row, shouldRefresh = true) => {
      const select = row.querySelector('[data-variant-select]');
      const sizeSelect = row.querySelector('[data-size-select]');
      const activeColor = row.querySelector('[data-color-value].is-active')?.dataset.colorValue || '';
      if (!select) return;
      let match = [...select.options].find((option) => !option.disabled && option.dataset.color === activeColor && (!sizeSelect || option.dataset.size === sizeSelect.value));
      if (!match) match = [...select.options].find((option) => !option.disabled && option.dataset.color === activeColor);
      if (!match) match = [...select.options].find((option) => !option.disabled);
      if (!match) return;
      select.value = match.value;
      if (sizeSelect && match.dataset.size) sizeSelect.value = match.dataset.size;
      showColorPreview(select);
      if (shouldRefresh) refreshSummary();
    };

    const updateLogoSize = (editor) => {
      const slider = editor.querySelector('[data-logo-size]');
      const output = editor.querySelector('[data-logo-size-output]');
      const logo = editor.querySelector('[data-proof-logo]');
      const inches = Number(slider.value);
      const isHat = editor.dataset.isHat === 'true';
      output.textContent = Number.isInteger(inches) ? String(inches) : inches.toFixed(1);
      logo.style.width = `${isHat ? inches * 9 : inches * 5}%`;
    };

    const setPlacement = (editor, requestedPlacement) => {
      const isHat = editor.dataset.isHat === 'true';
      const placement = isHat ? 'center-front' : requestedPlacement;
      const settings = placementSettings[placement];
      const stage = editor.querySelector('[data-logo-stage]');
      const logo = editor.querySelector('[data-proof-logo]');
      const slider = editor.querySelector('[data-logo-size]');
      const viewLabel = editor.querySelector('[data-proof-view-label]');
      const maximum = isHat ? 4 : settings.maximum;

      editor.dataset.logoPlacement = placement;
      stage.dataset.placement = placement;
      stage.dataset.view = settings.view;
      logo.style.left = `${settings.x}%`;
      logo.style.top = `${isHat ? 48 : settings.y}%`;
      slider.max = String(maximum);
      if (Number(slider.value) > maximum) slider.value = String(maximum);
      viewLabel.textContent = settings.view === 'back' ? 'Back view' : 'Front view';
      editor.querySelectorAll('[data-set-placement]').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.setPlacement === placement);
        button.setAttribute('aria-pressed', button.dataset.setPlacement === placement ? 'true' : 'false');
      });
      updateLogoSize(editor);
    };

    const refreshCardEditor = (card) => {
      const rows = selectedRows(card);
      const quantity = rows.reduce((sum, row) => sum + Math.max(0, Number(row.querySelector('[data-quantity]').value) || 0), 0);
      const editor = card.querySelector('[data-logo-editor]');
      const activeRow = rows.find((row) => Number(row.querySelector('[data-quantity]').value) > 0) || rows[0];
      const option = activeRow?.querySelector('[data-variant-select]')?.selectedOptions[0];
      if (!editor) return;
      editor.hidden = quantity === 0;
      editor.style.setProperty('--proof-color', previewColors[(option?.dataset.color || '').toLowerCase()] || '#3d4144');
      if (quantity > 0 && !editor.dataset.logoPlacement) {
        setPlacement(editor, editor.dataset.isHat === 'true' ? 'center-front' : 'left-chest');
      }
    };

    const loadCustomerLogo = () => {
      const file = fileInput?.files?.[0];
      if (!file) return;
      const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
      const allowedExtension = /\.(png|jpe?g|svg|webp)$/i.test(file.name);
      if ((!allowedTypes.includes(file.type) && !allowedExtension) || file.size > 10 * 1024 * 1024) {
        fileInput.value = '';
        message.textContent = 'Please upload a PNG, JPG, SVG or WebP logo no larger than 10 MB.';
        return;
      }
      if (customerLogoUrl && customerLogoIsObjectUrl) URL.revokeObjectURL(customerLogoUrl);
      customerLogoIsObjectUrl = true;
      displayCustomerLogo(URL.createObjectURL(file), 'Logo loaded. Review the placement and requested size on each selected garment.');
    };

    const refreshLogoSource = () => {
      const source = selectedLogoSource();
      builder.querySelectorAll('[data-logo-source-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.logoSourcePanel !== source;
      });
      if (logoSetupLine) logoSetupLine.hidden = source === 'saved';
      if (source === 'saved' && savedLogoSelect?.value) {
        if (customerLogoUrl && customerLogoIsObjectUrl) URL.revokeObjectURL(customerLogoUrl);
        customerLogoIsObjectUrl = false;
        const orderName = savedLogoSelect.selectedOptions[0]?.dataset.orderName || 'a previous order';
        displayCustomerLogo(savedLogoSelect.value, `Using your saved logo from ${orderName}. Review its placement on each selected garment.`);
      } else if (fileInput?.files?.length) {
        loadCustomerLogo();
      } else {
        displayCustomerLogo('', 'Upload a new logo to preview it on your selected garments.');
      }
    };

    builder.querySelectorAll('[data-logo-editor]').forEach((editor) => {
      const logo = editor.querySelector('[data-proof-logo]');
      const stage = editor.querySelector('[data-logo-stage]');
      const slider = editor.querySelector('[data-logo-size]');
      let dragging = false;

      editor.querySelectorAll('[data-set-placement]').forEach((button) => {
        button.addEventListener('click', () => setPlacement(editor, button.dataset.setPlacement));
      });
      slider.addEventListener('input', () => updateLogoSize(editor));

      logo.addEventListener('pointerdown', (event) => {
        if (logo.hidden || editor.dataset.logoPlacement === 'back-shoulders') return;
        dragging = true;
        logo.setPointerCapture(event.pointerId);
        logo.classList.add('is-dragging');
        event.preventDefault();
      });
      logo.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const bounds = stage.getBoundingClientRect();
        const x = Math.max(15, Math.min(85, ((event.clientX - bounds.left) / bounds.width) * 100));
        const y = Math.max(18, Math.min(75, ((event.clientY - bounds.top) / bounds.height) * 100));
        logo.style.left = `${x}%`;
        logo.style.top = `${y}%`;
      });
      logo.addEventListener('pointerup', (event) => {
        if (!dragging) return;
        dragging = false;
        logo.releasePointerCapture(event.pointerId);
        logo.classList.remove('is-dragging');
        const x = parseFloat(logo.style.left);
        const y = parseFloat(logo.style.top);
        const frontPlacements = ['left-chest', 'right-chest', 'center-front'];
        const nearest = frontPlacements.reduce((best, placement) => {
          const settings = placementSettings[placement];
          const distance = Math.hypot(x - settings.x, y - settings.y);
          return !best || distance < best.distance ? { placement, distance } : best;
        }, null);
        setPlacement(editor, nearest.placement);
      });
    });

    fileInput?.addEventListener('change', loadCustomerLogo);
    logoSourceInputs.forEach((input) => input.addEventListener('change', refreshLogoSource));
    savedLogoSelect?.addEventListener('change', refreshLogoSource);

    const refreshSummary = () => {
      let count = 0;
      let total = 0;
      const selectedLines = [];
      cards.forEach((card) => {
        selectedRows(card).forEach((row) => {
          const quantity = Math.max(0, Number(row.querySelector('[data-quantity]').value) || 0);
          const option = row.querySelector('[data-variant-select]').selectedOptions[0];
          count += quantity;
          total += quantity * Number(option?.dataset.price || 0);
          if (quantity > 0) selectedLines.push({ quantity, product: card.dataset.productName, variant: option?.dataset.variantTitle || option?.textContent.trim() || '' });
        });
        refreshCardEditor(card);
      });
      document.querySelectorAll('[data-selected-count]').forEach((el) => { el.textContent = String(count); });
      renderQuantityTier({
        count: count + cartEligibleQuantity,
        config: builderTierConfig,
        status: builder.querySelector('[data-tier-status]'),
        detail: builder.querySelector('[data-tier-progress-text]'),
        bar: builder.querySelector('[data-tier-progress-bar]')
      });
      const totalEl = document.querySelector('[data-apparel-total]');
      if (totalEl) totalEl.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(total / 100);
      if (draftLines) {
        draftLines.replaceChildren();
        if (!selectedLines.length) {
          const empty = document.createElement('li');
          empty.className = 'empty-selection';
          empty.textContent = 'Your selected items will appear here.';
          draftLines.append(empty);
        } else {
          selectedLines.forEach((line) => {
            const item = document.createElement('li');
            const title = document.createElement('span');
            const detail = document.createElement('small');
            title.textContent = `${line.quantity}× ${line.product}`;
            detail.textContent = line.variant;
            item.append(title, detail);
            draftLines.append(item);
          });
        }
      }
    };

    const setupVariantRow = (row) => {
      const input = row.querySelector('[data-quantity]');
      const sizeSelect = row.querySelector('[data-size-select]');
      row.querySelectorAll('[data-color-value]').forEach((button) => {
        button.addEventListener('click', () => {
          row.querySelectorAll('[data-color-value]').forEach((candidate) => {
            const active = candidate === button;
            candidate.classList.toggle('is-active', active);
            candidate.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          syncVariantRow(row);
        });
      });
      sizeSelect?.addEventListener('change', () => syncVariantRow(row));
      row.querySelector('[data-quantity-minus]').addEventListener('click', () => { input.value = Math.max(0, Number(input.value || 0) - 1); refreshSummary(); });
      row.querySelector('[data-quantity-plus]').addEventListener('click', () => { input.value = Math.min(100, Number(input.value || 0) + 1); refreshSummary(); });
      input.addEventListener('input', refreshSummary);
      row.querySelector('[data-remove-variant-row]')?.addEventListener('click', () => {
        row.remove();
        refreshSummary();
      });
      syncVariantRow(row, false);
    };

    cards.forEach((card) => {
      selectedRows(card).forEach(setupVariantRow);
      card.querySelector('[data-add-variant-row]')?.addEventListener('click', () => {
        const source = selectedRows(card)[0];
        const clone = source.cloneNode(true);
        clone.querySelector('[data-quantity]').value = '0';
        const remove = clone.querySelector('[data-remove-variant-row]');
        if (remove) remove.hidden = false;
        card.querySelector('[data-variant-rows]').append(clone);
        setupVariantRow(clone);
        clone.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });

    builder.addEventListener('submit', async (event) => {
      event.preventDefault();
      const logoSource = selectedLogoSource();
      const savedLogoOrder = savedLogoSelect?.selectedOptions[0]?.dataset.orderName || '';
      const items = cards.flatMap((card) => {
        const editor = card.querySelector('[data-logo-editor]');
        const size = editor?.querySelector('[data-logo-size]')?.value;
        const placement = editor?.dataset.logoPlacement || 'left-chest';
        return selectedRows(card).map((row) => ({
          id: Number(row.querySelector('[data-variant-select]').value),
          quantity: Math.max(0, Number(row.querySelector('[data-quantity]').value) || 0),
          properties: {
            'Logo placement': editor?.dataset.isHat === 'true' ? 'Front center' : placementNames[placement],
            'Requested logo width': `${size} inches`,
            'Logo source': logoSource === 'saved' ? `Saved logo from ${savedLogoOrder}` : 'New logo uploaded with this order',
            ...(logoSource === 'saved' ? { 'Saved logo file': savedLogoSelect.value } : {}),
            'Placement proof': 'Customer confirmed online — final proof required before production'
          }
        }));
      }).filter((item) => item.quantity > 0);
      if (!items.length) { message.textContent = 'Choose at least one apparel item.'; return; }
      if (logoSource === 'new' && (!logoForm || !fileInput?.files?.length)) { message.textContent = 'Upload your new logo before continuing.'; fileInput?.focus(); return; }
      if (logoSource === 'saved' && !savedLogoSelect?.value) { message.textContent = 'Choose one of your saved logos before continuing.'; savedLogoSelect?.focus(); return; }
      if (!proofConfirmation?.checked) { message.textContent = 'Review each selected item, then confirm the placement proof before continuing.'; proofConfirmation?.focus(); return; }
      message.textContent = 'Adding your apparel…';
      submit.disabled = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!response.ok) throw new Error('One or more selections are no longer available. Please check the quantities and try again.');
        if (logoSource === 'new') {
          message.textContent = 'Attaching your logo…';
          logoForm.submit();
        } else {
          message.textContent = 'Opening your cart…';
          window.location.href = root + 'cart';
        }
      } catch (error) {
        message.textContent = error.message || 'We could not add those items. Please try again.';
        submit.disabled = false;
      }
    });
    refreshLogoSource();
    refreshSummary();
  }

  const bundleForm = document.querySelector('#BundleProductForm');
  const bundleCustomizer = bundleForm?.querySelector('[data-bundle-logo-customizer]');
  if (bundleForm && bundleCustomizer) {
    const message = bundleCustomizer.querySelector('[data-bundle-logo-message]');
    const submit = bundleForm.querySelector('button[type="submit"]');
    const fileInput = document.querySelector('#BundleCustomerLogo');
    const logoSetupForm = document.querySelector('#BundleLogoSetupForm');
    const proofConfirmation = document.querySelector('#BundleProofConfirmation');
    const savedLogoSelect = document.querySelector('#BundleSavedLogoSelect');
    const logoSourceInputs = [...bundleCustomizer.querySelectorAll('input[name="bundle_logo_source"]')];
    const variantInput = bundleForm.querySelector('[name="id"]');
    const quantityInput = bundleForm.querySelector('[name="quantity"]');
    const editors = [...bundleCustomizer.querySelectorAll('[data-logo-editor]')];
    const placementNames = {
      'left-chest': 'Left chest',
      'right-chest': 'Right chest',
      'center-front': 'Middle front',
      'back-shoulders': 'Back between shoulders'
    };
    const placementSettings = {
      'left-chest': { x: 39, y: 33, maximum: 4.5, view: 'front' },
      'right-chest': { x: 61, y: 33, maximum: 4.5, view: 'front' },
      'center-front': { x: 50, y: 47, maximum: 10, view: 'front' },
      'back-shoulders': { x: 50, y: 30, maximum: 8, view: 'back' }
    };
    let customerLogoUrl = '';
    let customerLogoIsObjectUrl = false;

    const selectedLogoSource = () => {
      const checked = bundleCustomizer.querySelector('input[name="bundle_logo_source"]:checked');
      return checked?.value || bundleCustomizer.querySelector('input[name="bundle_logo_source"]')?.value || 'new';
    };

    const displayCustomerLogo = (url, statusMessage) => {
      customerLogoUrl = url;
      editors.forEach((editor) => {
        const logo = editor.querySelector('[data-proof-logo]');
        const empty = editor.querySelector('[data-proof-empty]');
        logo.src = url;
        logo.hidden = !url;
        empty.hidden = Boolean(url);
      });
      if (statusMessage) message.textContent = statusMessage;
    };

    const updateLogoSize = (editor) => {
      const slider = editor.querySelector('[data-logo-size]');
      const output = editor.querySelector('[data-logo-size-output]');
      const logo = editor.querySelector('[data-proof-logo]');
      const inches = Number(slider.value);
      output.textContent = Number.isInteger(inches) ? String(inches) : inches.toFixed(1);
      logo.style.width = `${editor.dataset.isHat === 'true' ? inches * 9 : inches * 5}%`;
    };

    const setPlacement = (editor, requestedPlacement) => {
      const isHat = editor.dataset.isHat === 'true';
      const placement = isHat ? 'center-front' : requestedPlacement;
      const settings = placementSettings[placement];
      const stage = editor.querySelector('[data-logo-stage]');
      const logo = editor.querySelector('[data-proof-logo]');
      const slider = editor.querySelector('[data-logo-size]');
      const viewLabel = editor.querySelector('[data-proof-view-label]');
      const maximum = isHat ? 4 : settings.maximum;

      editor.dataset.logoPlacement = placement;
      stage.dataset.placement = placement;
      stage.dataset.view = settings.view;
      logo.style.left = `${settings.x}%`;
      logo.style.top = `${isHat ? 48 : settings.y}%`;
      slider.max = String(maximum);
      if (Number(slider.value) > maximum) slider.value = String(maximum);
      viewLabel.textContent = settings.view === 'back' ? 'Back view' : 'Front view';
      editor.querySelectorAll('[data-set-placement]').forEach((button) => {
        const active = button.dataset.setPlacement === placement;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      updateLogoSize(editor);
    };

    const refreshBundleColor = () => {
      const option = variantInput?.selectedOptions?.[0];
      const selectedColor = (option?.dataset.color || '').toLowerCase();
      const color = previewColors[selectedColor] || '#3d4144';
      editors.forEach((editor) => editor.style.setProperty('--proof-color', color));
    };

    const loadCustomerLogo = () => {
      const file = fileInput?.files?.[0];
      if (!file) return;
      const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
      const allowedExtension = /\.(png|jpe?g|svg|webp)$/i.test(file.name);
      if ((!allowedTypes.includes(file.type) && !allowedExtension) || file.size > 10 * 1024 * 1024) {
        fileInput.value = '';
        displayCustomerLogo('', 'Please upload a PNG, JPG, SVG or WebP logo no larger than 10 MB.');
        return;
      }
      if (customerLogoUrl && customerLogoIsObjectUrl) URL.revokeObjectURL(customerLogoUrl);
      customerLogoIsObjectUrl = true;
      displayCustomerLogo(URL.createObjectURL(file), 'Logo loaded. Review the placement and requested size for every product type.');
    };

    const refreshLogoSource = () => {
      const source = selectedLogoSource();
      bundleCustomizer.querySelectorAll('[data-bundle-logo-source-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.bundleLogoSourcePanel !== source;
      });
      if (source === 'saved' && savedLogoSelect?.value) {
        if (customerLogoUrl && customerLogoIsObjectUrl) URL.revokeObjectURL(customerLogoUrl);
        customerLogoIsObjectUrl = false;
        const orderName = savedLogoSelect.selectedOptions[0]?.dataset.orderName || 'a previous order';
        displayCustomerLogo(savedLogoSelect.value, `Using your saved logo from ${orderName}. Review each placement before adding the bundle.`);
      } else if (fileInput?.files?.length) {
        loadCustomerLogo();
      } else {
        displayCustomerLogo('', 'Upload a new logo to preview it on the bundle garments.');
      }
    };

    editors.forEach((editor) => {
      const logo = editor.querySelector('[data-proof-logo]');
      const stage = editor.querySelector('[data-logo-stage]');
      const slider = editor.querySelector('[data-logo-size]');
      let dragging = false;

      setPlacement(editor, editor.dataset.isHat === 'true' ? 'center-front' : 'left-chest');
      editor.querySelectorAll('[data-set-placement]').forEach((button) => {
        button.addEventListener('click', () => setPlacement(editor, button.dataset.setPlacement));
      });
      slider.addEventListener('input', () => updateLogoSize(editor));
      logo.addEventListener('pointerdown', (event) => {
        if (logo.hidden || editor.dataset.logoPlacement === 'back-shoulders') return;
        dragging = true;
        logo.setPointerCapture(event.pointerId);
        logo.classList.add('is-dragging');
        event.preventDefault();
      });
      logo.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const bounds = stage.getBoundingClientRect();
        const x = Math.max(15, Math.min(85, ((event.clientX - bounds.left) / bounds.width) * 100));
        const y = Math.max(18, Math.min(75, ((event.clientY - bounds.top) / bounds.height) * 100));
        logo.style.left = `${x}%`;
        logo.style.top = `${y}%`;
      });
      logo.addEventListener('pointerup', (event) => {
        if (!dragging) return;
        dragging = false;
        logo.releasePointerCapture(event.pointerId);
        logo.classList.remove('is-dragging');
        const x = parseFloat(logo.style.left);
        const y = parseFloat(logo.style.top);
        const nearest = ['left-chest', 'right-chest', 'center-front'].reduce((best, placement) => {
          const settings = placementSettings[placement];
          const distance = Math.hypot(x - settings.x, y - settings.y);
          return !best || distance < best.distance ? { placement, distance } : best;
        }, null);
        setPlacement(editor, nearest.placement);
      });
    });

    fileInput?.addEventListener('change', loadCustomerLogo);
    logoSourceInputs.forEach((input) => input.addEventListener('change', refreshLogoSource));
    savedLogoSelect?.addEventListener('change', refreshLogoSource);
    if (variantInput?.matches('select')) variantInput.addEventListener('change', refreshBundleColor);

    bundleForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const logoSource = selectedLogoSource();
      if (logoSource === 'new' && bundleCustomizer.dataset.logoSetupAvailable !== 'true') {
        message.textContent = 'The Logo Setup product must be available before a new logo can be added.';
        return;
      }
      if (logoSource === 'new' && !fileInput?.files?.length) {
        message.textContent = 'Upload your new logo before adding this bundle.';
        fileInput?.focus();
        return;
      }
      if (logoSource === 'saved' && !savedLogoSelect?.value) {
        message.textContent = 'Choose one of your saved logos before adding this bundle.';
        savedLogoSelect?.focus();
        return;
      }
      if (!proofConfirmation?.checked) {
        message.textContent = 'Review the logo previews and confirm the placement proof before continuing.';
        proofConfirmation?.focus();
        return;
      }

      const properties = {
        'Logo source': logoSource === 'saved'
          ? `Saved logo from ${savedLogoSelect.selectedOptions[0]?.dataset.orderName || 'a previous order'}`
          : 'New logo uploaded with this order',
        'Placement proof': 'Customer confirmed online — final proof required before production'
      };
      if (logoSource === 'saved') properties['Saved logo file'] = savedLogoSelect.value;
      editors.forEach((editor) => {
        const label = editor.dataset.productName;
        const placement = editor.dataset.isHat === 'true' ? 'Front center' : placementNames[editor.dataset.logoPlacement || 'left-chest'];
        const size = editor.querySelector('[data-logo-size]').value;
        properties[`${label} placement`] = placement;
        properties[`${label} logo width`] = `${size} inches`;
      });

      message.textContent = 'Adding your customized bundle…';
      submit.disabled = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(variantInput.value),
              quantity: Math.max(1, Number(quantityInput?.value) || 1),
              properties
            }]
          })
        });
        if (!response.ok) throw new Error('This bundle or one of its included items is no longer available. Please review the options and try again.');
        if (logoSource === 'new') {
          message.textContent = 'Attaching your logo…';
          logoSetupForm.submit();
        } else {
          message.textContent = 'Opening your cart…';
          window.location.href = root + 'cart';
        }
      } catch (error) {
        message.textContent = error.message || 'We could not add this bundle. Please try again.';
        submit.disabled = false;
      }
    });

    refreshBundleColor();
    refreshLogoSource();
  }

  const extraForm = document.querySelector('#ExtraProductForm');
  const extraCustomizer = extraForm?.querySelector('[data-extra-logo-customizer]');
  if (extraForm && extraCustomizer) {
    const message = extraCustomizer.querySelector('[data-extra-logo-message]');
    const submit = extraForm.querySelector('button[type="submit"]');
    const fileInput = document.querySelector('#ExtraCustomerLogo');
    const logoSetupForm = document.querySelector('#ExtraLogoSetupForm');
    const savedLogoSelect = document.querySelector('#ExtraSavedLogoSelect');
    const sourceInputs = [...extraCustomizer.querySelectorAll('input[name="extra_logo_source"]')];
    const proofConfirmation = document.querySelector('#ExtraProofConfirmation');
    const placementSelect = document.querySelector('#ExtraPlacement');
    const sizeInput = document.querySelector('[data-extra-logo-size]');
    const sizeOutput = document.querySelector('[data-extra-size-output]');
    const previewLogo = document.querySelector('[data-extra-preview-logo]');
    const previewEmpty = document.querySelector('[data-extra-preview-empty]');
    let objectLogoUrl = '';

    const selectedSource = () => {
      const checked = extraCustomizer.querySelector('input[name="extra_logo_source"]:checked');
      return checked?.value || extraCustomizer.querySelector('input[name="extra_logo_source"]')?.value || 'new';
    };

    const displayLogo = (url, status = '') => {
      previewLogo.src = url || '';
      previewLogo.hidden = !url;
      previewEmpty.hidden = Boolean(url);
      if (status) message.textContent = status;
    };

    const updatePreviewSettings = () => {
      const width = Number(sizeInput?.value || 3);
      if (sizeOutput) sizeOutput.textContent = Number.isInteger(width) ? String(width) : width.toFixed(1);
      if (previewLogo) previewLogo.style.width = `${Math.max(14, Math.min(42, width * 6))}%`;
      const placement = placementSelect?.value || 'Recommended placement';
      const handle = extraCustomizer.dataset.productHandle || '';
      if (placement === 'Centered') {
        previewLogo.style.left = '50%';
        previewLogo.style.top = '52%';
      } else if (handle.includes('golf-towel')) {
        previewLogo.style.left = '50%';
        previewLogo.style.top = '57%';
      } else if (handle.includes('tote')) {
        previewLogo.style.left = '50%';
        previewLogo.style.top = '55%';
      } else {
        previewLogo.style.left = '50%';
        previewLogo.style.top = '50%';
      }
    };

    const loadNewLogo = () => {
      const file = fileInput?.files?.[0];
      if (!file) return displayLogo('', 'Upload a logo to see the approximate preview.');
      const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
      const allowedExtension = /\.(png|jpe?g|svg|webp)$/i.test(file.name);
      if ((!allowedTypes.includes(file.type) && !allowedExtension) || file.size > 10 * 1024 * 1024) {
        fileInput.value = '';
        displayLogo('', 'Please upload a PNG, JPG, SVG or WebP logo no larger than 10 MB.');
        return;
      }
      if (objectLogoUrl) URL.revokeObjectURL(objectLogoUrl);
      objectLogoUrl = URL.createObjectURL(file);
      displayLogo(objectLogoUrl, 'Logo loaded. Review the approximate placement and requested size.');
      updatePreviewSettings();
    };

    const refreshExtraLogoSource = () => {
      const source = selectedSource();
      extraCustomizer.querySelectorAll('[data-extra-logo-source-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.extraLogoSourcePanel !== source;
      });
      if (source === 'saved' && savedLogoSelect?.value) {
        if (objectLogoUrl) URL.revokeObjectURL(objectLogoUrl);
        objectLogoUrl = '';
        const orderName = savedLogoSelect.selectedOptions[0]?.dataset.orderName || 'a previous order';
        displayLogo(savedLogoSelect.value, `Using your saved logo from ${orderName}.`);
      } else if (fileInput?.files?.length) {
        loadNewLogo();
      } else {
        displayLogo('', 'Upload a logo to see the approximate preview.');
      }
      updatePreviewSettings();
    };

    fileInput?.addEventListener('change', loadNewLogo);
    savedLogoSelect?.addEventListener('change', refreshExtraLogoSource);
    sourceInputs.forEach((input) => input.addEventListener('change', refreshExtraLogoSource));
    placementSelect?.addEventListener('change', updatePreviewSettings);
    sizeInput?.addEventListener('input', updatePreviewSettings);

    extraForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const source = selectedSource();
      if (source === 'new' && extraCustomizer.dataset.logoSetupAvailable !== 'true') {
        message.textContent = 'The Logo Setup product must be available before a new logo can be added.';
        return;
      }
      if (source === 'new' && !fileInput?.files?.length) {
        message.textContent = 'Upload your new logo before adding this item.';
        fileInput?.focus();
        return;
      }
      if (source === 'saved' && !savedLogoSelect?.value) {
        message.textContent = 'Choose one of your saved logos before adding this item.';
        savedLogoSelect?.focus();
        return;
      }
      if (!proofConfirmation?.checked) {
        message.textContent = 'Review the preview and confirm the proof statement before continuing.';
        proofConfirmation?.focus();
        return;
      }

      const variantInput = extraForm.querySelector('[name="id"]');
      const quantityInput = extraForm.querySelector('[name="quantity"]');
      const savedOrder = savedLogoSelect?.selectedOptions?.[0]?.dataset.orderName || 'a previous order';
      const properties = {
        'Logo source': source === 'saved' ? `Saved logo from ${savedOrder}` : 'New logo uploaded with this order',
        'Logo placement': placementSelect?.value || 'Recommended placement',
        'Requested logo width': `${sizeInput?.value || 3} inches`,
        'Placement proof': 'Customer confirmed online — final proof required before production'
      };
      if (source === 'saved') properties['Saved logo file'] = savedLogoSelect.value;

      submit.disabled = true;
      message.textContent = 'Adding your customized item…';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(variantInput.value),
              quantity: Math.max(1, Number(quantityInput?.value) || 1),
              properties
            }]
          })
        });
        if (!response.ok) throw new Error('This item or option is no longer available. Please review your selection.');
        if (source === 'new') {
          message.textContent = 'Attaching your logo…';
          logoSetupForm.submit();
        } else {
          window.location.href = root + 'cart';
        }
      } catch (error) {
        message.textContent = error.message || 'We could not add this item. Please try again.';
        submit.disabled = false;
      }
    });

    refreshExtraLogoSource();
  }

  const cartForm = document.querySelector('#CartForm');
  if (cartForm) {
    const cartTierConfig = quantityPricingConfig(cartForm);
    const refreshCartQuantityTier = () => {
      const eligibleCount = [...cartForm.querySelectorAll('[data-cart-item][data-eligible-apparel="true"]')]
        .reduce((total, item) => total + Math.max(0, Number(item.querySelector('[data-cart-quantity]')?.value || 0)), 0);
      renderQuantityTier({
        count: eligibleCount,
        config: cartTierConfig,
        status: cartForm.querySelector('[data-cart-tier-status]'),
        detail: cartForm.querySelector('[data-cart-tier-progress-text]'),
        bar: cartForm.querySelector('[data-cart-tier-progress-bar]')
      });
    };
    cartForm.querySelectorAll('[data-cart-quantity]').forEach((input) => input.addEventListener('input', refreshCartQuantityTier));
    refreshCartQuantityTier();
  }

  const checkoutButton = document.querySelector('[data-shopify-checkout]');
  if (checkoutButton) {
    checkoutButton.addEventListener('click', async () => {
      const form = document.querySelector('#CartForm');
      const message = document.querySelector('[data-cart-message]');
      checkoutButton.disabled = true;
      message.textContent = 'Updating your order…';
      try {
        const data = new FormData(form);
        const updates = data.getAll('updates[]').map(Number);
        const note = data.get('note') || '';
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates, note }) });
        if (!response.ok) throw new Error('Could not update the cart.');
        const code = document.querySelector('#DiscountCode').value.trim();
        window.location.href = code ? root + 'discount/' + encodeURIComponent(code) + '?redirect=/checkout' : root + 'checkout';
      } catch (error) {
        message.textContent = error.message || 'Could not continue to checkout.';
        checkoutButton.disabled = false;
      }
    });
  }
});
